import {
  AchievementStatus,
  QualityImprovementItem,
  QualityReportForm,
  QualityReportResultItem,
} from '../../types/qualityPolicy';
import { emptyImprovement, emptyResult } from '../../utils/qualityPolicyForms';
import QualityDocMetaFields from './QualityDocMetaFields';

interface Props {
  year: number;
  value: QualityReportForm;
  disabled?: boolean;
  onChange: (next: QualityReportForm) => void;
  onSyncFromPlan?: () => void;
}

function QualityReportFormView({ year, value, disabled, onChange, onSyncFromPlan }: Props) {
  const updateResult = (id: string, patch: Partial<QualityReportResultItem>) => {
    onChange({
      ...value,
      results: value.results.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    });
  };

  const removeResult = (id: string) => {
    const next = value.results.filter((row) => row.id !== id);
    onChange({ ...value, results: next.length ? next : [emptyResult()] });
  };

  const updateImprovement = (id: string, patch: Partial<QualityImprovementItem>) => {
    onChange({
      ...value,
      improvements: value.improvements.map((row) =>
        row.id === id ? { ...row, ...patch } : row
      ),
    });
  };

  const removeImprovement = (id: string) => {
    const next = value.improvements.filter((row) => row.id !== id);
    onChange({ ...value, improvements: next.length ? next : [emptyImprovement()] });
  };

  return (
    <div className="qform">
      <p className="qform-lead">
        {year}년 품질방침 보고서 — 목표 대비 실적, 미달 사유·조치, 개선과제, 차기 반영사항을
        작성합니다.
      </p>

      <QualityDocMetaFields
        meta={value.meta}
        disabled={disabled}
        onChange={(meta) => onChange({ ...value, meta })}
      />

      <div className="qform-section">
        <h3>보고 기간</h3>
        <div className="qform-grid qform-grid--2">
          <label>
            시작일
            <input
              type="date"
              value={value.periodFrom}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, periodFrom: e.target.value })}
            />
          </label>
          <label>
            종료일
            <input
              type="date"
              value={value.periodTo}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, periodTo: e.target.value })}
            />
          </label>
        </div>
      </div>

      <div className="qform-section">
        <div className="qform-section__head">
          <h3>목표 대비 실적</h3>
          <div className="ncr-actions">
            {!disabled && onSyncFromPlan && (
              <button type="button" className="ncr-btn is-secondary" onClick={onSyncFromPlan}>
                추진계획 목표 불러오기
              </button>
            )}
            {!disabled && (
              <button
                type="button"
                className="ncr-btn is-secondary"
                onClick={() => onChange({ ...value, results: [...value.results, emptyResult()] })}
              >
                실적 추가
              </button>
            )}
          </div>
        </div>
        <div className="qform-table-wrap">
          <table className="qform-table qform-table--wide">
            <thead>
              <tr>
                <th>목표코드</th>
                <th>목표명</th>
                <th>목표값</th>
                <th>실적</th>
                <th>달성률</th>
                <th>판정</th>
                <th>미달/특이사유</th>
                <th>조치</th>
                {!disabled && <th />}
              </tr>
            </thead>
            <tbody>
              {value.results.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      value={row.objectiveCode}
                      disabled={disabled}
                      onChange={(e) => updateResult(row.id, { objectiveCode: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.objectiveTitle}
                      disabled={disabled}
                      onChange={(e) => updateResult(row.id, { objectiveTitle: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.target}
                      disabled={disabled}
                      onChange={(e) => updateResult(row.id, { target: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.actual}
                      disabled={disabled}
                      onChange={(e) => updateResult(row.id, { actual: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.achievementRate}
                      disabled={disabled}
                      placeholder="예: 95%"
                      onChange={(e) => updateResult(row.id, { achievementRate: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      value={row.resultStatus}
                      disabled={disabled}
                      onChange={(e) =>
                        updateResult(row.id, {
                          resultStatus: e.target.value as AchievementStatus,
                        })
                      }
                    >
                      <option value="">선택</option>
                      <option value="ACHIEVED">달성</option>
                      <option value="PARTIAL">부분달성</option>
                      <option value="FAILED">미달</option>
                    </select>
                  </td>
                  <td>
                    <input
                      value={row.cause}
                      disabled={disabled}
                      onChange={(e) => updateResult(row.id, { cause: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.action}
                      disabled={disabled}
                      onChange={(e) => updateResult(row.id, { action: e.target.value })}
                    />
                  </td>
                  {!disabled && (
                    <td>
                      <button
                        type="button"
                        className="qform-row-del"
                        onClick={() => removeResult(row.id)}
                      >
                        삭제
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="qform-section">
        <div className="qform-section__head">
          <h3>개선과제</h3>
          {!disabled && (
            <button
              type="button"
              className="ncr-btn is-secondary"
              onClick={() =>
                onChange({
                  ...value,
                  improvements: [...value.improvements, emptyImprovement()],
                })
              }
            >
              과제 추가
            </button>
          )}
        </div>
        <div className="qform-table-wrap">
          <table className="qform-table">
            <thead>
              <tr>
                <th>개선항목</th>
                <th>담당</th>
                <th>완료예정</th>
                <th>상태</th>
                {!disabled && <th />}
              </tr>
            </thead>
            <tbody>
              {value.improvements.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      value={row.item}
                      disabled={disabled}
                      onChange={(e) => updateImprovement(row.id, { item: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.owner}
                      disabled={disabled}
                      onChange={(e) => updateImprovement(row.id, { owner: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={row.dueDate}
                      disabled={disabled}
                      onChange={(e) => updateImprovement(row.id, { dueDate: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.status}
                      disabled={disabled}
                      placeholder="진행중/완료"
                      onChange={(e) => updateImprovement(row.id, { status: e.target.value })}
                    />
                  </td>
                  {!disabled && (
                    <td>
                      <button
                        type="button"
                        className="qform-row-del"
                        onClick={() => removeImprovement(row.id)}
                      >
                        삭제
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="qform-section">
        <h3>경영검토 요약</h3>
        <textarea
          rows={4}
          value={value.managementReview}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, managementReview: e.target.value })}
          placeholder="경영검토 결과, 의사결정 사항"
        />
      </div>

      <div className="qform-section">
        <h3>차기년도 반영사항</h3>
        <textarea
          rows={4}
          value={value.nextYearPlan}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, nextYearPlan: e.target.value })}
          placeholder="차기 품질방침·목표에 반영할 사항"
        />
      </div>

      <div className="qform-section">
        <h3>비고</h3>
        <textarea
          rows={3}
          value={value.remarks}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, remarks: e.target.value })}
        />
      </div>
    </div>
  );
}

export default QualityReportFormView;
